// @flow

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {GetApp} from '@material-ui/icons';
import {
  CircularProgress,
  Divider,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import HorizontalFilter from '../../../../elements/HorizontalFilter/HorizontalFilter';
import HorizontalScroll from '../../../../elements/HorizontalFilter/HorizontalScroll/HorizontalScroll';
import FilterButton from './FilterButton/FilterButton';
import RowItemSales from './RowItem/RowItemSales';
import PromotionsSalesModal from '../Modals/PromotionsSalesModal';
import {commaFormat} from '../../../../utils/number';

import {endpoints} from '../../api';

import type {DashboardAction} from '../../duck';
import type {Currency} from '../../../../entities';
import type {PromotionSalesData} from '../../duck';

const useStyles = makeStyles(theme => ({
  section: {
    height: '100%',
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.grey900,
  },
  btnDownload: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  divider: {
    backgroundColor: '#E8E8ED',
    margin: theme.spacing(2, 0),
  },
  seeMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  seeMore: {
    color: colors.grey800,
    fontSize: 14,
  },
  skeleton: {
    borderRadius: 12,
  },
  advice: {
    fontSize: 14,
    color: colors.grey700,
  },
  wrapFilter: {
    '& > div': {
      height: '48px!important',
    },
  },
  emptyChartBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#ffffff10',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartBackdropSales: {
    backgroundColor: 'rgba(255, 255, 255, 0.6)!important',
  },
  emptyChartAlert: {
    background: colors.grey200,
    borderRadius: '24px',
    width: 260,
    padding: theme.spacing(3),
    textAlign: 'center',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  chartAlertSales: {
    backgroundColor: '#FFFFFF!important',
    padding: theme.spacing(3, 2),
    boxShadow: '0px 4px 16px rgba(55, 61, 76, 0.16)',
    borderRadius: '12px',
  },
  textAlertSale: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}));

type Props = {
  canShowDisabled: boolean,
  currency?: Currency,
  promotionSales?: PromotionSalesData[],
  isFetchingPromotionsSales?: boolean,
  buildCommonQueryParamsCallback: () => any,
  fetchPromotionSales: (payload: DashboardAction) => void,
};
export default function PromotionsSection(props: Props) {
  const {
    canShowDisabled,
    currency,
    promotionSales,
    isFetchingPromotionsSales,
    buildCommonQueryParamsCallback,
    fetchPromotionSales,
  } = props;

  const classes = useStyles();
  const [promotionsFilter, setPromotionsFilter] = useState('promotion');
  const [openPromotionsModal, setOpenPromotionsModal] = useState(false);

  const buildQueryParamsCallback = useCallback(() => {
    let params: {[key: string]: any} = {};
    if (!!promotionsFilter) {
      params.type = promotionsFilter;
    }
    return params;
  }, [promotionsFilter]);

  const shouldShowEmptyAlert =
    promotionSales && !promotionSales.length && canShowDisabled;

  const promotionsFilterActive = promotionsFilter === 'promotion';

  const alertContent = (
    <>
      <Typography className={classes.textAlertSale}>
        ¡Haz crecer tus ventas creando{' '}
        {promotionsFilterActive ? 'promociones' : 'cupones'}!
      </Typography>
      <CustomButton
        to={`/marketing/${promotionsFilterActive ? 'promotions' : 'coupons'}`}
        variant="contained"
      >
        Crear {promotionsFilterActive ? 'Promoción' : 'Cupón'}
      </CustomButton>
    </>
  );

  useEffect(() => {
    if (buildQueryParamsCallback) {
      const dateRange = buildCommonQueryParamsCallback();
      const queryParams = buildQueryParamsCallback();
      if (dateRange && queryParams) {
        let queryFilter = {
          ...queryParams,
          init_date: dateRange.start_date,
          end_date: dateRange.end_date,
        };
        fetchPromotionSales({queryFilter});
      }
    }
  }, [
    buildCommonQueryParamsCallback,
    buildQueryParamsCallback,
    fetchPromotionSales,
  ]);

  const handleOpenModal = useCallback(() => {
    setOpenPromotionsModal(true);
  }, []);

  const handleUpdateFilter = (value: string) => {
    if (value) setPromotionsFilter(value);
  };

  const fileDownloadUrl = useMemo(() => {
    const dateRange = buildCommonQueryParamsCallback();
    const {start_date: init_date, end_date} = dateRange;
    const endpoint =
      promotionsFilter === 'promotion'
        ? endpoints.GET_PROMOTION_SALES
        : endpoints.GET_COUPON_SALES;
    return `${endpoint}?init_date=${init_date}&end_date=${end_date}&format=xlsx`;
  }, [buildCommonQueryParamsCallback, promotionsFilter]);

  return (
    <div className={classes.section}>
      <div>
        <div className={classes.header}>
          <Typography className={classes.title}>
            Ventas por promoción
          </Typography>
          <div>
            <CustomButton
              startIcon={<GetApp />}
              className={classes.btnDownload}
              href={fileDownloadUrl}
              variant="contained"
              target="_blank"
              rel="noopener noreferrer"
            >
              Descargar
            </CustomButton>
          </div>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.wrapFilter}>
          <HorizontalFilter noMargin>
            <HorizontalScroll>
              <FilterButton
                id="promotion"
                text="Promociones"
                selected={promotionsFilter.includes('promotion')}
                handleOnClick={handleUpdateFilter}
              />
              <FilterButton
                id="coupon"
                text="Cupones"
                selected={promotionsFilter.includes('coupon')}
                handleOnClick={handleUpdateFilter}
              />
            </HorizontalScroll>
          </HorizontalFilter>
        </div>
        <div
          style={{
            position: 'relative',
            minHeight: shouldShowEmptyAlert ? '10rem' : undefined,
          }}
        >
          {shouldShowEmptyAlert && (
            <div
              className={`${classes.emptyChartBackdrop} ${classes.chartBackdropSales}`}
            >
              <div
                className={`${classes.emptyChartAlert} ${classes.chartAlertSales}`}
              >
                {alertContent}
              </div>
            </div>
          )}
          {isFetchingPromotionsSales ? (
            Array.from({length: 1}, (x, i) => (
              <div
                key={i}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress />
              </div>
            ))
          ) : promotionSales && promotionSales.length ? (
            promotionSales
              .slice(0, 5)
              .map((c, idx) => (
                <RowItemSales
                  key={idx}
                  id={idx + 1}
                  text={
                    promotionsFilter.includes('promotion')
                      ? c.name
                      : c.code || ''
                  }
                  price={commaFormat(
                    c.amount ? c.amount : 0,
                    currency ? currency.symbol : ''
                  )}
                />
              ))
          ) : (
            <Typography className={classes.advice}>
              No existen ventas.
            </Typography>
          )}
        </div>

        {promotionSales && promotionSales.length > 5 ? (
          <>
            <Divider className={classes.divider} />
            <div className={classes.seeMoreContainer}>
              <CustomButton
                onClick={handleOpenModal}
                className={classes.seeMore}
              >
                Ver más
              </CustomButton>
            </div>
          </>
        ) : null}
      </div>
      <PromotionsSalesModal
        currency={currency}
        open={openPromotionsModal}
        setOpen={setOpenPromotionsModal}
        fetchPromotionSales={fetchPromotionSales}
      />
    </div>
  );
}

PromotionsSection.propTypes = {
  canShowDisabled: PropTypes.bool.isRequired,
  currency: PropTypes.object,
  promotionSales: PropTypes.array,
  isFetchingPromotionsSales: PropTypes.bool,
  buildCommonQueryParamsCallback: PropTypes.func.isRequired,
  fetchPromotionSales: PropTypes.func.isRequired,
};
